import { useState, useEffect, useRef } from 'react'
import { Header } from '../components/layout/Header'

interface ImportJob {
  id: string
  name: string
  importType: string
  fileType: string
  fileName: string
  fileSize: number
  status: string
  progress: number
  totalRows: number
  processedRows: number
  successRows: number
  errorRows: number
  errors: { row: number; field: string; error: string }[]
  createdBy: string
  createdAt: string
  completedAt: string | null
}

interface ImportTemplate {
  id: string
  name: string
  importType: string
  fileType: string
  fieldMappings: Record<string, string>
  options: Record<string, unknown>
  createdBy: string
  createdAt: string
}

interface ImportType {
  id: string
  name: string
  description: string
  fields: string[]
}

interface FileType {
  id: string
  name: string
  extension: string
  mimeType: string
}

interface ValidationResult {
  valid: boolean
  errors: string[]
  warnings: string[]
  preview: string[][]
  detectedColumns: string[]
  rowCount: number
}

interface ImportStats {
  totalJobs: number
  completedJobs: number
  failedJobs: number
  processingJobs: number
  totalRowsImported: number
  totalErrors: number
  byType: Record<string, number>
  successRate: number
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://192.168.0.98:8090/api'

export default function DataImport() {
  const [activeTab, setActiveTab] = useState<'upload' | 'jobs' | 'templates' | 'stats'>('upload')
  const [jobs, setJobs] = useState<ImportJob[]>([])
  const [templates, setTemplates] = useState<ImportTemplate[]>([])
  const [importTypes, setImportTypes] = useState<ImportType[]>([])
  const [fileTypes, setFileTypes] = useState<FileType[]>([])
  const [stats, setStats] = useState<ImportStats | null>(null)
  const [loading, setLoading] = useState(true)

  // Upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [selectedImportType, setSelectedImportType] = useState('')
  const [uploadStep, setUploadStep] = useState<'select' | 'validate' | 'map' | 'import'>('select')
  const [validation, setValidation] = useState<ValidationResult | null>(null)
  const [fieldMappings, setFieldMappings] = useState<Record<string, string>>({})
  const [importName, setImportName] = useState('')
  const [importing, setImporting] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<string>('')

  const fileInputRef = useRef<HTMLInputElement>(null)

  // Get current user
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}')
  const userId = currentUser.email || 'john.doe@expandly.com'

  useEffect(() => {
    fetchImportTypes()
    fetchFileTypes()
    fetchJobs()
    fetchTemplates()
    fetchStats()
  }, [])

  const fetchImportTypes = async () => {
    try {
      const response = await fetch(`${API_BASE}/data-import/import-types`)
      if (response.ok) {
        const data = await response.json()
        setImportTypes(data)
      }
    } catch (error) {
      console.error('Failed to fetch import types:', error)
    }
  }

  const fetchFileTypes = async () => {
    try {
      const response = await fetch(`${API_BASE}/data-import/file-types`)
      if (response.ok) {
        const data = await response.json()
        setFileTypes(data)
      }
    } catch (error) {
      console.error('Failed to fetch file types:', error)
    }
  }

  const fetchJobs = async () => {
    setLoading(true)
    try {
      const response = await fetch(`${API_BASE}/data-import/jobs?limit=50`)
      if (response.ok) {
        const data = await response.json()
        setJobs(data.jobs || [])
      }
    } catch (error) {
      console.error('Failed to fetch jobs:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API_BASE}/data-import/templates`)
      if (response.ok) {
        const data = await response.json()
        setTemplates(data)
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error)
    }
  }

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/data-import/statistics`)
      if (response.ok) {
        const data = await response.json()
        setStats(data)
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      setImportName(file.name.replace(/\.[^/.]+$/, ''))
    }
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) {
      setSelectedFile(file)
      setImportName(file.name.replace(/\.[^/.]+$/, ''))
    }
  }

  const handleValidate = async () => {
    if (!selectedFile || !selectedImportType) return

    setLoading(true)
    try {
      const formData = new FormData()
      formData.append('file', selectedFile)

      const response = await fetch(
        `${API_BASE}/data-import/validate?import_type=${selectedImportType}`,
        {
          method: 'POST',
          body: formData,
        }
      )

      if (response.ok) {
        const data = await response.json()
        setValidation(data)

        if (data.valid) {
          // Initialize field mappings
          const importType = importTypes.find(t => t.id === selectedImportType)
          const mappings: Record<string, string> = {}
          if (importType) {
            importType.fields.forEach(field => {
              // Try to auto-match columns
              const matchingColumn = data.detectedColumns.find(
                (col: string) => col.toLowerCase().includes(field.toLowerCase()) ||
                  field.toLowerCase().includes(col.toLowerCase())
              )
              mappings[field] = matchingColumn || ''
            })
          }
          setFieldMappings(mappings)
          setUploadStep('map')
        }
      }
    } catch (error) {
      console.error('Validation failed:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleStartImport = async () => {
    if (!selectedFile) return

    setImporting(true)
    try {
      // Create import job
      const response = await fetch(`${API_BASE}/data-import/jobs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: importName,
          import_type: selectedImportType,
          file_type: selectedFile.name.split('.').pop() || 'csv',
          file_name: selectedFile.name,
          file_size: selectedFile.size,
          field_mappings: fieldMappings,
          options: {},
          user: userId,
        }),
      })

      if (response.ok) {
        const job = await response.json()

        // Start the import
        await fetch(`${API_BASE}/data-import/jobs/${job.id}/start`, {
          method: 'POST',
        })

        // Reset form
        setSelectedFile(null)
        setSelectedImportType('')
        setUploadStep('select')
        setValidation(null)
        setFieldMappings({})
        setImportName('')

        // Refresh jobs
        fetchJobs()
        fetchStats()
        setActiveTab('jobs')
      }
    } catch (error) {
      console.error('Import failed:', error)
    } finally {
      setImporting(false)
    }
  }

  const handleCancelJob = async (jobId: string) => {
    try {
      await fetch(`${API_BASE}/data-import/jobs/${jobId}/cancel`, {
        method: 'POST',
      })
      fetchJobs()
    } catch (error) {
      console.error('Failed to cancel job:', error)
    }
  }

  const handleDeleteJob = async (jobId: string) => {
    try {
      await fetch(`${API_BASE}/data-import/jobs/${jobId}`, {
        method: 'DELETE',
      })
      fetchJobs()
      fetchStats()
    } catch (error) {
      console.error('Failed to delete job:', error)
    }
  }

  const handleDeleteTemplate = async (templateId: string) => {
    try {
      await fetch(`${API_BASE}/data-import/templates/${templateId}`, {
        method: 'DELETE',
      })
      fetchTemplates()
    } catch (error) {
      console.error('Failed to delete template:', error)
    }
  }

  const handleLoadTemplate = (template: ImportTemplate) => {
    setSelectedTemplate(template.id)
    setSelectedImportType(template.importType)
    setFieldMappings(template.fieldMappings)
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: '#6b7280',
      validating: '#3b82f6',
      processing: '#8b5cf6',
      completed: '#10b981',
      failed: '#ef4444',
      cancelled: '#f59e0b',
    }
    return colors[status] || '#6b7280'
  }

  const getStatusBgColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'rgba(107, 114, 128, 0.1)',
      validating: 'rgba(59, 130, 246, 0.1)',
      processing: 'rgba(139, 92, 246, 0.1)',
      completed: 'rgba(16, 185, 129, 0.1)',
      failed: 'rgba(239, 68, 68, 0.1)',
      cancelled: 'rgba(245, 158, 11, 0.1)',
    }
    return colors[status] || 'rgba(107, 114, 128, 0.1)'
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F9FAFB' }}>
      <Header />
      <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>
            Data Import
          </h1>
          <p style={{ color: '#64748b', fontSize: '14px' }}>
            Upload and import data files into the system
          </p>
        </div>

        {/* Stats Summary */}
        {stats && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              padding: '16px'
            }}>
              <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Total Imports</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b' }}>{stats.totalJobs}</div>
            </div>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              padding: '16px'
            }}>
              <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Rows Imported</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#10b981' }}>{stats.totalRowsImported.toLocaleString()}</div>
            </div>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              padding: '16px'
            }}>
              <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Success Rate</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#3b82f6' }}>{stats.successRate}%</div>
            </div>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              padding: '16px'
            }}>
              <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Processing</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#8b5cf6' }}>{stats.processingJobs}</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '4px',
          marginBottom: '24px',
          background: '#f1f5f9',
          padding: '4px',
          borderRadius: '10px',
          width: 'fit-content'
        }}>
          {[
            { id: 'upload', label: 'Upload', icon: '📤' },
            { id: 'jobs', label: 'Import Jobs', icon: '📋' },
            { id: 'templates', label: 'Templates', icon: '📁' },
            { id: 'stats', label: 'Statistics', icon: '📊' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                background: activeTab === tab.id ? 'white' : 'transparent',
                color: activeTab === tab.id ? '#1e293b' : '#64748b',
                fontWeight: activeTab === tab.id ? '600' : '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: activeTab === tab.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Upload Tab */}
        {activeTab === 'upload' && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            padding: '24px'
          }}>
            {/* Step Indicator */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '32px',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                top: '20px',
                left: '60px',
                right: '60px',
                height: '2px',
                background: '#e2e8f0'
              }} />
              {[
                { id: 'select', label: 'Select File', num: 1 },
                { id: 'validate', label: 'Validate', num: 2 },
                { id: 'map', label: 'Map Fields', num: 3 },
                { id: 'import', label: 'Import', num: 4 }
              ].map((step, _idx) => (
                <div
                  key={step.id}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: uploadStep === step.id ? '#3b82f6' :
                      ['select', 'validate', 'map', 'import'].indexOf(uploadStep) > ['select', 'validate', 'map', 'import'].indexOf(step.id) ? '#10b981' : '#e2e8f0',
                    color: uploadStep === step.id || ['select', 'validate', 'map', 'import'].indexOf(uploadStep) > ['select', 'validate', 'map', 'import'].indexOf(step.id) ? 'white' : '#64748b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '600',
                    marginBottom: '8px'
                  }}>
                    {step.num}
                  </div>
                  <span style={{
                    fontSize: '13px',
                    color: uploadStep === step.id ? '#1e293b' : '#64748b',
                    fontWeight: uploadStep === step.id ? '600' : '400'
                  }}>
                    {step.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Step 1: Select File */}
            {uploadStep === 'select' && (
              <div>
                {/* Import Type Selection */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                    Import Type
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                    {importTypes.map(type => (
                      <button
                        key={type.id}
                        onClick={() => setSelectedImportType(type.id)}
                        style={{
                          padding: '16px',
                          borderRadius: '8px',
                          border: selectedImportType === type.id ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                          background: selectedImportType === type.id ? 'rgba(59, 130, 246, 0.05)' : 'white',
                          cursor: 'pointer',
                          textAlign: 'left'
                        }}
                      >
                        <div style={{ fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>{type.name}</div>
                        <div style={{ fontSize: '13px', color: '#64748b' }}>{type.description}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Use Template */}
                {templates.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                      Or use a template
                    </label>
                    <select
                      value={selectedTemplate}
                      onChange={(e) => {
                        const template = templates.find(t => t.id === e.target.value)
                        if (template) handleLoadTemplate(template)
                      }}
                      style={{
                        width: '100%',
                        maxWidth: '400px',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        fontSize: '14px'
                      }}
                    >
                      <option value="">Select a template...</option>
                      {templates.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* File Drop Zone */}
                <div
                  onDrop={handleDrop}
                  onDragOver={(e) => e.preventDefault()}
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    border: '2px dashed #e2e8f0',
                    borderRadius: '12px',
                    padding: '48px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: selectedFile ? '#f0fdf4' : '#f8fafc',
                    transition: 'all 0.2s'
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.json,.xlsx,.xml"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  {selectedFile ? (
                    <>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
                      <div style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>
                        {selectedFile.name}
                      </div>
                      <div style={{ color: '#64748b' }}>{formatFileSize(selectedFile.size)}</div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>📁</div>
                      <div style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
                        Drop your file here or click to browse
                      </div>
                      <div style={{ color: '#64748b' }}>
                        Supports: {fileTypes.map(t => t.extension).join(', ')}
                      </div>
                    </>
                  )}
                </div>

                <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      if (selectedFile && selectedImportType) {
                        setUploadStep('validate')
                        handleValidate()
                      }
                    }}
                    disabled={!selectedFile || !selectedImportType}
                    style={{
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: 'none',
                      background: selectedFile && selectedImportType ? '#3b82f6' : '#94a3b8',
                      color: 'white',
                      fontWeight: '600',
                      cursor: selectedFile && selectedImportType ? 'pointer' : 'not-allowed'
                    }}
                  >
                    Validate File
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Validate */}
            {uploadStep === 'validate' && (
              <div style={{ textAlign: 'center', padding: '48px' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  border: '4px solid #e2e8f0',
                  borderTopColor: '#3b82f6',
                  margin: '0 auto 24px',
                  animation: 'spin 1s linear infinite'
                }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
                  Validating File...
                </h3>
                <p style={{ color: '#64748b' }}>Checking file format and structure</p>
              </div>
            )}

            {/* Step 3: Map Fields */}
            {uploadStep === 'map' && validation && (
              <div>
                {/* Validation Summary */}
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  marginBottom: '24px'
                }}>
                  <div style={{
                    flex: 1,
                    padding: '16px',
                    background: validation.valid ? '#f0fdf4' : '#fef2f2',
                    borderRadius: '8px',
                    border: `1px solid ${validation.valid ? '#bbf7d0' : '#fecaca'}`
                  }}>
                    <div style={{
                      fontWeight: '600',
                      color: validation.valid ? '#166534' : '#dc2626',
                      marginBottom: '4px'
                    }}>
                      {validation.valid ? 'File Valid' : 'Validation Errors'}
                    </div>
                    <div style={{ fontSize: '13px', color: '#64748b' }}>
                      {validation.rowCount} rows detected, {validation.detectedColumns.length} columns
                    </div>
                  </div>
                </div>

                {/* Warnings */}
                {validation.warnings.length > 0 && (
                  <div style={{
                    padding: '12px 16px',
                    background: '#fffbeb',
                    borderRadius: '8px',
                    border: '1px solid #fde68a',
                    marginBottom: '24px'
                  }}>
                    <div style={{ fontWeight: '500', color: '#92400e', marginBottom: '4px' }}>Warnings:</div>
                    {validation.warnings.map((w, i) => (
                      <div key={i} style={{ fontSize: '13px', color: '#92400e' }}>{w}</div>
                    ))}
                  </div>
                )}

                {/* Field Mapping */}
                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
                    Map Columns to Fields
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                    {Object.entries(fieldMappings).map(([field, column]) => (
                      <div key={field}>
                        <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>
                          {field.replace(/_/g, ' ').replace(/^./, s => s.toUpperCase())}
                        </label>
                        <select
                          value={column}
                          onChange={(e) => setFieldMappings(prev => ({ ...prev, [field]: e.target.value }))}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            fontSize: '14px'
                          }}
                        >
                          <option value="">-- Skip --</option>
                          {validation.detectedColumns.map(col => (
                            <option key={col} value={col}>{col}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Preview */}
                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
                    Data Preview
                  </h4>
                  <div style={{ overflow: 'auto', maxHeight: '300px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ background: '#f8fafc' }}>
                          {validation.preview[0]?.map((col, i) => (
                            <th key={i} style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {validation.preview.slice(1, 6).map((row, i) => (
                          <tr key={i}>
                            {row.map((cell, j) => (
                              <td key={j} style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9' }}>
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Import Name */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                    Import Name
                  </label>
                  <input
                    type="text"
                    value={importName}
                    onChange={(e) => setImportName(e.target.value)}
                    placeholder="Give this import a name..."
                    style={{
                      width: '100%',
                      maxWidth: '400px',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <button
                    onClick={() => {
                      setUploadStep('select')
                      setValidation(null)
                    }}
                    style={{
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      background: 'white',
                      color: '#374151',
                      cursor: 'pointer'
                    }}
                  >
                    Back
                  </button>
                  <button
                    onClick={handleStartImport}
                    disabled={importing || !importName}
                    style={{
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: 'none',
                      background: importing || !importName ? '#94a3b8' : '#10b981',
                      color: 'white',
                      fontWeight: '600',
                      cursor: importing || !importName ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {importing ? 'Starting Import...' : 'Start Import'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Jobs Tab */}
        {activeTab === 'jobs' && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            overflow: 'hidden'
          }}>
            <div style={{ maxHeight: '600px', overflow: 'auto' }}>
              {loading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  Loading jobs...
                </div>
              ) : jobs.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  No import jobs found
                </div>
              ) : (
                jobs.map(job => (
                  <div
                    key={job.id}
                    style={{
                      padding: '20px',
                      borderBottom: '1px solid #f1f5f9',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px'
                    }}
                  >
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '10px',
                      background: getStatusBgColor(job.status),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px',
                      flexShrink: 0
                    }}>
                      {job.status === 'completed' ? '✓' :
                       job.status === 'failed' ? '✗' :
                       job.status === 'processing' ? '⟳' : '○'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ fontWeight: '600', color: '#1e293b' }}>{job.name}</span>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500',
                          textTransform: 'capitalize',
                          background: getStatusBgColor(job.status),
                          color: getStatusColor(job.status)
                        }}>
                          {job.status}
                        </span>
                      </div>
                      <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>
                        {job.fileName} · {formatFileSize(job.fileSize)} · {job.importType}
                      </div>
                      {job.status === 'processing' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{
                            flex: 1,
                            height: '6px',
                            background: '#e2e8f0',
                            borderRadius: '3px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${job.progress}%`,
                              height: '100%',
                              background: '#3b82f6',
                              borderRadius: '3px',
                              transition: 'width 0.3s'
                            }} />
                          </div>
                          <span style={{ fontSize: '12px', color: '#64748b' }}>{job.progress}%</span>
                        </div>
                      )}
                      {job.status === 'completed' && (
                        <div style={{ fontSize: '13px', color: '#10b981' }}>
                          {job.successRows.toLocaleString()} rows imported
                          {job.errorRows > 0 && (
                            <span style={{ color: '#ef4444', marginLeft: '8px' }}>
                              ({job.errorRows} errors)
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {job.status === 'processing' && (
                        <button
                          onClick={() => handleCancelJob(job.id)}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '8px',
                            border: '1px solid #fecaca',
                            background: 'white',
                            color: '#ef4444',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Cancel
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteJob(job.id)}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          background: 'white',
                          color: '#64748b',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            overflow: 'hidden'
          }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>Import Templates</h3>
              <p style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
                Save field mappings as templates for repeated imports
              </p>
            </div>
            <div>
              {templates.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  No templates saved yet
                </div>
              ) : (
                templates.map(template => (
                  <div
                    key={template.id}
                    style={{
                      padding: '16px 20px',
                      borderBottom: '1px solid #f1f5f9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: '500', color: '#1e293b', marginBottom: '4px' }}>{template.name}</div>
                      <div style={{ fontSize: '13px', color: '#64748b' }}>
                        Type: {template.importType} · Format: {template.fileType.toUpperCase()}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => {
                          handleLoadTemplate(template)
                          setActiveTab('upload')
                        }}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '8px',
                          border: '1px solid #3b82f6',
                          background: 'white',
                          color: '#3b82f6',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        Use
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(template.id)}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '8px',
                          border: '1px solid #fecaca',
                          background: 'white',
                          color: '#ef4444',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Statistics Tab */}
        {activeTab === 'stats' && stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
            {/* Import by Type */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              padding: '24px'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
                Imports by Type
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {Object.entries(stats.byType).map(([type, count]) => (
                  <div key={type} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#374151', textTransform: 'capitalize' }}>{type.replace(/_/g, ' ')}</span>
                    <span style={{
                      padding: '4px 12px',
                      background: '#f1f5f9',
                      borderRadius: '20px',
                      fontWeight: '600',
                      color: '#1e293b'
                    }}>
                      {count}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Job Status Breakdown */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              padding: '24px'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
                Job Status
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#374151' }}>Completed</span>
                  <span style={{ color: '#10b981', fontWeight: '600' }}>{stats.completedJobs}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#374151' }}>Processing</span>
                  <span style={{ color: '#8b5cf6', fontWeight: '600' }}>{stats.processingJobs}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#374151' }}>Failed</span>
                  <span style={{ color: '#ef4444', fontWeight: '600' }}>{stats.failedJobs}</span>
                </div>
              </div>
            </div>

            {/* Data Quality */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              padding: '24px'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
                Data Quality
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#374151' }}>Total Rows Imported</span>
                  <span style={{ color: '#1e293b', fontWeight: '600' }}>{stats.totalRowsImported.toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#374151' }}>Total Errors</span>
                  <span style={{ color: '#ef4444', fontWeight: '600' }}>{stats.totalErrors.toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#374151' }}>Success Rate</span>
                  <span style={{ color: '#10b981', fontWeight: '600' }}>{stats.successRate}%</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
